import { useState, useEffect, useMemo } from 'react'
import {
  Search,
  ChevronRight,
  Loader2,
  Grid,
  List,
  X,
  Copy,
  Check,
  Terminal,
  Code2,
  Sparkles,
  Zap,
  Globe,
  Database,
  Cloud,
  Cpu,
  Shield,
  MessageSquare,
  Calendar,
  FileText,
  Image,
  Settings,
  Wrench,
  BookOpen
} from 'lucide-react'
import SEO from '../components/SEO'
import './Skills.css'
import api from '../utils/api'
import useSiteLanguage from '../hooks/useSiteLanguage'

// 分类图标映射
const iconMap = {
  'AI & LLMs': Sparkles,
  'Web & Frontend Development': Globe,
  'Browser & Automation': Terminal,
  'Coding Agents & IDEs': Code2,
  'DevOps & Cloud': Cloud,
  'Search & Research': Search,
  'Productivity & Tasks': Check,
  'Communication': MessageSquare,
  'CLI Utilities': Wrench,
  'Git & GitHub': Database,
  'Security & Passwords': Shield,
  'Image & Video Generation': Image,
  'Media & Streaming': FileText,
  'Notes & PKM': BookOpen,
  'PDF & Documents': FileText,
  'Calendar & Scheduling': Calendar,
  'Marketing & Sales': Zap,
  'Health & Fitness': Settings,
  'Transportation': Settings,
  'Smart Home & IoT': Cpu,
  'Gaming': Settings,
  'Personal Development': Settings,
  'Data & Analytics': Database,
  'Apple Apps & Services': Settings,
  'Self-Hosted & Automation': Cloud,
  'Finance': Settings,
  'iOS & macOS Development': Settings,
  'Agent-to-Agent Protocols': MessageSquare,
  'Clawdbot Tools': Terminal,
  'Moltbook': BookOpen,
  'Shopping & E-commerce': Shopping,
  'Speech & Transcription': MessageSquare
}

// Shopping 图标
function Shopping(props) {
  return (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="21" r="1"/>
      <circle cx="20" cy="21" r="1"/>
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
    </svg>
  )
}

function Skills() {
  const [skills, setSkills] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState(undefined)
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState('grid')
  const [copiedId, setCopiedId] = useState(null)
  const [error, setError] = useState(null)
  const [showToast, setShowToast] = useState(false)
  const [toastSkill, setToastSkill] = useState(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0
  })
  const { lang } = useSiteLanguage()

  const text = {
    zh: {
      seoTitle: '技能中心',
      seoDescription: '浏览和安装丰富的DClaw技能插件，扩展你的AI助手能力。发现社区贡献的100+技能，让你的智能助手更加强大。',
      heroPrefix: '发现和安装',
      heroSuffix: '技能',
      searchPlaceholder: '搜索技能名称或描述...',
      gridView: '网格视图',
      listView: '列表视图',
      retry: '重新加载',
      loading: '加载技能中...',
      noResult: '没有找到相关技能',
      noResultDesc: '尝试使用不同的关键词或清除筛选条件',
      clearFilters: '清除筛选',
      copied: '已复制',
      install: '安装',
      details: '详情',
      previous: '上一页',
      next: '下一页',
      pageInfo: (page, totalPages) => `第 ${page} / ${totalPages} 页`,
      noDesc: '暂无描述',
      unnamed: '未命名技能',
      loadFailed: '加载失败',
      loadSkillsFailed: '加载技能数据失败，请稍后重试',
      incompleteSkill: '技能数据不完整',
      installToast: (name) => `已经复制「${name}」安装指令，请粘贴给你的 Bot，它会自动完成安装!`
    },
    en: {
      seoTitle: 'Skills',
      seoDescription: 'Browse and install rich DClaw skills to expand your AI assistant with community-provided capabilities.',
      heroPrefix: 'Discover and Install',
      heroSuffix: 'Skills',
      searchPlaceholder: 'Search skills by name or description...',
      gridView: 'Grid',
      listView: 'List',
      retry: 'Retry',
      loading: 'Loading skills...',
      noResult: 'No skills found',
      noResultDesc: 'Try different keywords or clear filters',
      clearFilters: 'Clear filters',
      copied: 'Copied',
      install: 'Install',
      details: 'Details',
      previous: 'Previous',
      next: 'Next',
      pageInfo: (page, totalPages) => `Page ${page} / ${totalPages}`,
      noDesc: 'No description',
      unnamed: 'Unnamed Skill',
      loadFailed: 'Load failed',
      loadSkillsFailed: 'Failed to load skills. Please try again later.',
      incompleteSkill: 'Skill data is incomplete',
      installToast: (name) => `Copied the install command for "${name}". Paste it to your Bot and it will complete the installation automatically!`
    },
    fr: {
      seoTitle: 'Compétences',
      seoDescription: 'Parcourez et installez les compétences DClaw pour enrichir votre assistant IA.',
      heroPrefix: 'Découvrir et installer',
      heroSuffix: 'Compétences',
      searchPlaceholder: 'Rechercher par nom ou description...',
      gridView: 'Grille',
      listView: 'Liste',
      retry: 'Réessayer',
      loading: 'Chargement des compétences...',
      noResult: 'Aucune compétence trouvée',
      noResultDesc: 'Essayez un autre mot-clé ou effacez les filtres',
      clearFilters: 'Effacer les filtres',
      copied: 'Copié',
      install: 'Installer',
      details: 'Détails',
      previous: 'Précédent',
      next: 'Suivant',
      pageInfo: (page, totalPages) => `Page ${page} / ${totalPages}`,
      noDesc: 'Aucune description',
      unnamed: 'Compétence sans nom',
      loadFailed: 'Échec du chargement',
      loadSkillsFailed: 'Impossible de charger les compétences pour le moment.',
      incompleteSkill: 'Les données de la compétence sont incomplètes',
      installToast: (name) => `La commande d’installation pour "${name}" a été copiée. Collez-la dans votre bot pour terminer l’installation automatiquement.`
    },
    de: {
      seoTitle: 'Funktionen',
      seoDescription: 'Durchsuchen und installieren Sie DClaw-Funktionen, um Ihren KI-Assistenten zu erweitern.',
      heroPrefix: 'Entdecken und installieren',
      heroSuffix: 'Funktionen',
      searchPlaceholder: 'Nach Name oder Beschreibung suchen...',
      gridView: 'Raster',
      listView: 'Liste',
      retry: 'Erneut laden',
      loading: 'Funktionen werden geladen...',
      noResult: 'Keine Funktionen gefunden',
      noResultDesc: 'Versuchen Sie andere Suchbegriffe oder löschen Sie die Filter',
      clearFilters: 'Filter löschen',
      copied: 'Kopiert',
      install: 'Installieren',
      details: 'Details',
      previous: 'Zurück',
      next: 'Weiter',
      pageInfo: (page, totalPages) => `Seite ${page} / ${totalPages}`,
      noDesc: 'Keine Beschreibung',
      unnamed: 'Unbenannte Funktion',
      loadFailed: 'Laden fehlgeschlagen',
      loadSkillsFailed: 'Funktionen konnten derzeit nicht geladen werden.',
      incompleteSkill: 'Funktionsdaten sind unvollständig',
      installToast: (name) => `Der Installationsbefehl für "${name}" wurde kopiert. Fügen Sie ihn in Ihren Bot ein, damit die Installation automatisch abgeschlossen wird.`
    }
  }[lang]

  // 加载分类数据
  const loadCategories = async () => {
    try {
      const response = await api.get(`/skills/categories?search=${encodeURIComponent(searchQuery)}`)
      const data = response.data

      if (data.success) {
        setCategories(data.data || data.categories || [])
        // 首次加载默认选中第一个分类（精选）
        if ((data.data || data.categories || []).length > 0 && selectedCategory === undefined) {
          setSelectedCategory((data.data || data.categories)[0].key)
        }
      }
    } catch (error) {
      console.error('加载分类失败:', error)
    }
  }

  // 加载技能数据
  const loadSkills = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const params = new URLSearchParams()
      if (selectedCategory && selectedCategory !== 'all') {
        params.append('category', selectedCategory)
      }
      if (searchQuery) {
        params.append('search', searchQuery)
      }
      params.append('page', pagination.page.toString())
      params.append('limit', pagination.limit.toString())
      
      const response = await api.get(`/skills?${params}`)
      const data = response.data
      
      if (data.success) {
        setSkills(data.data || data.skills || [])
        if (data.pagination) {
          setPagination(data.pagination)
        }
      } else {
        setError(data.error || text.loadFailed)
      }
    } catch (error) {
      console.error('加载技能失败:', error)
      setError(text.loadSkillsFailed)
    } finally {
      setLoading(false)
    }
  }

  // 初始加载和筛选变化时重新加载
  useEffect(() => {
    loadCategories()
  }, [searchQuery])

  useEffect(() => {
    loadSkills()
  }, [selectedCategory, searchQuery, pagination.page])

  // 获取分类图标
  const getCategoryIcon = (categoryKey) => {
    const IconComponent = iconMap[categoryKey] || Settings
    return <IconComponent className="w-4 h-4" />
  }

  // 生成安装提示文本
  const generateInstallText = (skill) => {
    const name = skill.name
    const desc = lang === 'zh'
      ? (skill.description_zh || skill.description || text.noDesc)
      : (skill.description || skill.description_zh || text.noDesc)
    
    // 如果技能存在 model_id，则使用专属的拉取安装命令
    if (skill.model_id) {
      if (lang === 'zh') {
        return `帮我安装 OpenClaw 技能「${name}」（${desc}）。请下载技能包：${skill.url}`
      }
      return `Help me install the OpenClaw skill "${name}" (${desc}). Please download the skill package from: ${skill.url}`
    }

    // 否则使用默认的社区安装命令
    const url = skill.url || `https://clawskills.sh/skills/${name}`
    if (lang === 'zh') {
      return `帮我安装 OpenClaw 技能「${name}」（${desc}）。优先用命令：npx clawhub@latest install ${name}；如果限流或失败，备选方案：fetch ${url} 找到页面里的 raw.githubusercontent.com SKILL.md 链接，fetch 后按说明安装。`
    }
    return `Help me install the OpenClaw skill "${name}" (${desc}). Prefer the command: npx clawhub@latest install ${name}. If rate limited or it fails, use the fallback: fetch ${url}, find the raw.githubusercontent.com SKILL.md link on the page, fetch it, and install according to the instructions.`
  }

  // 复制安装命令
  const copyInstallCmd = (skill) => {
    if (!skill || !skill.name) {
      console.error(text.incompleteSkill, skill)
      return
    }
    const text = generateInstallText(skill)
    navigator.clipboard.writeText(text)
    setCopiedId(skill.id)
    setToastSkill(skill)
    setShowToast(true)
    setTimeout(() => {
      setCopiedId(null)
      setShowToast(false)
    }, 3000)
  }

  // 获取技能描述（根据当前语言）
  const getSkillDescription = (skill) => {
    const desc = lang === 'zh' ? (skill.description_zh || skill.description) : (skill.description || skill.description_zh)
    return desc ? String(desc).trim() : text.noDesc
  }

  // 获取技能名称（根据当前语言）
  const getSkillName = (skill) => {
    const name = lang === 'zh' ? (skill.name_zh || skill.name) : (skill.name || skill.name_zh)
    return name ? String(name).trim() : text.unnamed
  }

  // 获取分类名称（根据当前语言）
  const getCategoryName = (category) => {
    return lang === 'zh' ? (category.name || category.key) : (category.key || category.name)
  }

  // 切换页码
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }))
    }
  }

  return (
    <div className="skills-page">
      <SEO
        title={text.seoTitle}
        description={text.seoDescription}
        keywords="小龙虾， openclaw，一键安装"
      />
      {/* 简化的 Hero Section */}
      <section className="skills-hero">
        <div className="hero-content">
          <h1 className="hero-title text-2xl sm:text-3xl md:text-4xl">
            {text.heroPrefix} <span className="gradient-text">DClaw {text.heroSuffix}</span>
          </h1>

          
          {/* Search Box */}
          <div className="hero-search-row flex-col sm:flex-row">
            <div className="hero-search w-full">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder={text.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setPagination(prev => ({ ...prev, page: 1 }))
                }}
              />
              {searchQuery && (
                <button 
                  className="clear-search"
                  onClick={() => setSearchQuery('')}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 横向分类筛选栏 */}
      <div className="category-bar">
        <div className="category-bar-content overflow-x-auto flex-nowrap sm:flex-wrap">
          {categories.filter(c => c.key !== 'all' && (searchQuery || c.search_count > 0)).map((cat) => (
            <button
              key={cat.key}
              onClick={() => setSelectedCategory(selectedCategory === cat.key ? '' : cat.key)}
              className={`category-chip ${selectedCategory === cat.key ? 'active' : ''}`}
            >
              {getCategoryIcon(cat.key)}
              <span>{getCategoryName(cat)}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="skills-container">
        {/* Main Content */}
        <main className="skills-main">
          {/* Toolbar */}
          <div className="skills-toolbar">
            <div className="view-toggle">
              <button
                onClick={() => setViewMode('grid')}
                className={viewMode === 'grid' ? 'active' : ''}
                title={text.gridView}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'active' : ''}
                title={text.listView}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="error-state">
              <p>{error}</p>
              <button onClick={loadSkills} className="btn-retry">
                {text.retry}
              </button>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="loading-state">
              <Loader2 className="w-8 h-8 animate-spin" />
              <p>{text.loading}</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && skills.length === 0 && (
            <div className="empty-state">
              <Search className="w-12 h-12" />
              <h3>{text.noResult}</h3>
              <p>{text.noResultDesc}</p>
              <button 
                className="btn-primary"
                onClick={() => {setSearchQuery(''); setSelectedCategory('')}}
              >
                {text.clearFilters}
              </button>
            </div>
          )}

          {/* Skills Grid */}
          {!loading && !error && skills.length > 0 && viewMode === 'grid' && (
            <div className="skills-grid">
              {skills.map((skill) => (
                <div key={skill.id} className="skill-card">
                  <div className="skill-card-header">
                    <span className="skill-category">
                      {getCategoryName(categories.find(c => c.key === skill.skill_cate) || { key: skill.skill_cate, name: skill.skill_cate_zh || skill.skill_cate })}
                    </span>
                  </div>
                  
                  <h3 className="skill-name">{getSkillName(skill)}</h3>
                  <p className="skill-description">
                    {getSkillDescription(skill).slice(0, 100)}
                    {getSkillDescription(skill).length > 100 ? '...' : ''}
                  </p>
                  
                  <div className="skill-actions">
                    <button 
                      className="btn-install"
                      onClick={() => copyInstallCmd(skill)}
                      disabled={!skill.name}
                    >
                      {copiedId === skill.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copiedId === skill.id ? `${text.copied}「${getSkillName(skill)}」` : text.install}
                    </button>
                    {skill.url ? (
                      <a 
                        href={skill.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-details"
                      >
                        {text.details}
                        <ChevronRight className="w-4 h-4" />
                      </a>
                    ) : (
                      <span className="btn-details disabled">{text.details}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Skills List */}
          {!loading && !error && skills.length > 0 && viewMode === 'list' && (
            <div className="skills-list">
              {skills.map((skill) => (
                <div key={skill.id} className="skill-list-item">
                  <div className="skill-info">
                    <h3 className="skill-name">{getSkillName(skill)}</h3>
                    <p className="skill-description">
                      {getSkillDescription(skill).slice(0, 150)}
                      {getSkillDescription(skill).length > 150 ? '...' : ''}
                    </p>
                    <div className="skill-tags">
                      <span className="tag">
                        {getCategoryName(categories.find(c => c.key === skill.skill_cate) || { key: skill.skill_cate, name: skill.skill_cate_zh || skill.skill_cate })}
                      </span>
                    </div>
                  </div>
                  <div className="skill-actions-row">
                    <button 
                      className="btn-install-sm"
                      onClick={() => copyInstallCmd(skill)}
                      disabled={!skill.name}
                    >
                      {copiedId === skill.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copiedId === skill.id ? `${text.copied}「${getSkillName(skill)}」` : text.install}
                    </button>
                    {skill.url ? (
                      <a 
                        href={skill.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-details-sm"
                      >
                        {text.details}
                      </a>
                    ) : (
                      <span className="btn-details-sm disabled">{text.details}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {!loading && !error && pagination.totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="btn-page"
              >
                {text.previous}
              </button>
              <span className="page-info">
                {text.pageInfo(pagination.page, pagination.totalPages)}
              </span>
              <button 
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="btn-page"
              >
                {text.next}
              </button>
            </div>
          )}
        </main>
      </div>
      
      {/* Toast Notification */}
      {showToast && toastSkill && (
        <div className="toast-notification">
          <div className="toast-content">
            <Check className="w-5 h-5" />
            <p>
              {text.installToast(getSkillName(toastSkill))}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default Skills
